import React, { useEffect, useMemo, useState } from 'react'
import { Header } from 'components'
import { Box, Grid, Typography, Stack, TextField, Divider, Fab } from '@mui/material'
import { defaultRoutes } from 'pages'
import { useMutation } from '@tanstack/react-query'
import { API, Auth } from 'aws-amplify'
import { LoadingButton } from '@mui/lab'
import { Download } from '@mui/icons-material'
import { Storage } from 'aws-amplify'

import { ProgressIndicator } from 'components/maintenanceSelector/wearapp/components/ProgressIndicator'
import { saveAs } from 'file-saver'
import { useGetLegalDocuments } from 'hooks/useGetLegalDocuments'
import { useSnackbar } from 'notistack'
import { useCurrentUser } from 'hooks'

const apiName = 'AdminQueries'

const getHeaders = async () => {
  return {
    'Content-Type': 'application/json',
    Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
  }
}

const LegalDocumentUrl = ({ placeholder, value = '', onChange }) => {
  return (
    <Grid>
      <Grid container spacing={2} flexDirection="row" padding={2} alignItems="center">
        <Grid md={4} item>
          <Typography variant="h6">{placeholder}</Typography>
        </Grid>
        <Grid md={6} item>
          <TextField
            value={value}
            onChange={({ target: { value } }) => onChange(value)}
            sx={{ ml: '0.5em' }}
            placeholder={placeholder}
            fullWidth
          />
        </Grid>
        <Grid item>
          <Fab
            color="primary"
            size="small"
            onClick={async () => {
              const file = await Storage.get(`legal-documents/${value}`, { download: true })

              saveAs(file.Body, value)
            }}>
            <Download size="small" />
          </Fab>
        </Grid>
      </Grid>
      <Divider />
    </Grid>
  )
}

export const LegalRequirment = () => {
  const [legalDocuments, setLegalDocuments] = useState({
    privacyPolicyLocation: '',
    eulDocumentLocation: ''
  })

  const { enqueueSnackbar } = useSnackbar()

  const {
    data: { eulDocument, privacyPolicy },
    query
  } = useGetLegalDocuments()

  useEffect(() => {
    setLegalDocuments({
      privacyPolicyLocation: privacyPolicy ?? '',
      eulDocumentLocation: eulDocument ?? ''
    })
  }, [eulDocument, privacyPolicy])

  const saveLegalDocumentsLocation = useMutation({
    mutationFn: async () => {
      return API.put(apiName, '/legal', {
        headers: await getHeaders(),
        body: {
          privacyPolicyDocumentLocation: legalDocuments.privacyPolicyLocation,
          eulDocumentLocation: legalDocuments.eulDocumentLocation
        }
      })
    },
    onSuccess: () => {
      query.refetch()
      enqueueSnackbar('Legal documents have been successfully saved')
    },
    onError: () => {
      enqueueSnackbar('An error occurred while saving legal documents', {
        variant: 'error'
      })
    },
    retry: false
  })

  const isButtonDisabled =
    saveLegalDocumentsLocation.isLoading ||
    query.isFetching ||
    query.isLoading ||
    legalDocuments.privacyPolicyLocation.trim().length === 0 ||
    legalDocuments.eulDocumentLocation.trim().length === 0 ||
    (legalDocuments.privacyPolicyLocation === privacyPolicy && legalDocuments.eulDocumentLocation === eulDocument)

  const onLegalDocumentUrlChange = (key) => {
    return (val) => {
      setLegalDocuments((current) => {
        return {
          ...current,
          [key]: val
        }
      })
    }
  }

  const { currentUser } = useCurrentUser()
  const navRoutes = useMemo(() => {
    return defaultRoutes({
      isAdmin: currentUser.isAdmin,
      canAccessRoles: currentUser.canAccessRoles,
      canAccessUsers: currentUser.canAccessUsers
    })
  }, [currentUser.isAdmin, currentUser.canAccessRoles, currentUser.canAccessUsers])

  return (
    <Box sx={{ px: '15px' }}>
      <Header routes={navRoutes} />
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid item md={12}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }} mt={1} mb={1}>
            <Typography variant="h1">Legal</Typography>
          </Stack>
          <Divider />
        </Grid>
      </Grid>
      {query.isInitialLoading && (
        <Grid container justifyContent="center" mt={2} mb={2} maxWidth="60%">
          <Grid item md={5}>
            <ProgressIndicator />
          </Grid>
        </Grid>
      )}

      <Grid flexDirection="column" container padding={6} columnGap={1} maxWidth="60%">
        <Grid item>
          <LegalDocumentUrl
            value={legalDocuments.privacyPolicyLocation}
            placeholder="Privacy document"
            onChange={onLegalDocumentUrlChange('privacyPolicyLocation')}
          />
        </Grid>

        <Grid item>
          <LegalDocumentUrl
            value={legalDocuments.eulDocumentLocation}
            placeholder="EUL document"
            onChange={onLegalDocumentUrlChange('eulDocumentLocation')}
          />
        </Grid>

        <Grid justifyContent="flex-end" container item mt={1}>
          <LoadingButton
            disabled={isButtonDisabled}
            loading={saveLegalDocumentsLocation.isLoading}
            variant="contained"
            color="secondary"
            onClick={() => saveLegalDocumentsLocation.mutate()}>
            Save
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  )
}
